import { environment } from '@environment';
import {
  Auth,
  IdTokenResult,
  OAuthProvider,
  Unsubscribe,
  User,
  confirmPasswordReset,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  verifyPasswordResetCode,
} from 'firebase/auth';

import {
  Injectable,
  NgZone,
  WritableSignal,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';

import { FIREBASE_AUTH } from '../../../../utils/firebase-auth';

export type { IdTokenResult, OAuthProvider, User } from 'firebase/auth';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly _router: Router = inject(Router);
  private readonly _auth: Auth = inject(FIREBASE_AUTH);
  private readonly _ngZone = inject(NgZone);

  public isLoading: WritableSignal<boolean> = signal(false);

  public createOAuthProvider(providerId: string): OAuthProvider {
    return new OAuthProvider(providerId);
  }

  public onLogin(authProvider: OAuthProvider): void {
    this.isLoading.set(true);
    signInWithPopup(this._auth, authProvider)
      .then(() => {
        this._ngZone.run(() => {
          this.isLoading.set(false);
          void this._router.navigate(['/']);
        });
      })
      .catch(() => {
        this._ngZone.run(() => this.isLoading.set(false));
      });
  }

  public async resetPassword(email: string): Promise<string | null> {
    try {
      await sendPasswordResetEmail(this._auth, email);
      return null;
    } catch (e: any) {
      if (e.code === 'auth/user-not-found') {
        return 'EMAIL_NOT_FOUND';
      }
      return 'UNKNOWN_ERROR';
    }
  }

  public async loginWithPassword(
    email: string,
    password: string
  ): Promise<string | null> {
    this.isLoading.set(true);
    try {
      await signInWithEmailAndPassword(this._auth, email, password);
      this.isLoading.set(false);
      this._ngZone.run(() => void this._router.navigate(['/']));
      return null;
    } catch (e: any) {
      this.isLoading.set(false);
      switch (e.code) {
        case 'auth/user-not-found':
          return 'EMAIL_NOT_FOUND';
        case 'auth/wrong-password':
          return 'INVALID_PASSWORD';
        default:
          return 'UNKNOWN_ERROR';
      }
    }
  }

  public currentUser(): User | null {
    return this._auth.currentUser;
  }

  public authStateReady(): Promise<void> {
    return this._auth.authStateReady();
  }

  public onAuthStateChanged(
    callback: (user: User | null) => void
  ): Unsubscribe {
    return this._auth.onAuthStateChanged(callback);
  }

  public async getIdToken(): Promise<string | undefined> {
    return this._auth.currentUser?.getIdToken();
  }

  public async getIdTokenResult(): Promise<IdTokenResult | undefined> {
    return this._auth.currentUser?.getIdTokenResult();
  }

  public async getUserPermissions(): Promise<string[]> {
    const token = await this.getIdTokenResult();
    if (token && token.claims['permissions']) {
      return token.claims['permissions'] as string[];
    }
    return [];
  }

  public async logOut(): Promise<void> {
    const token = await this.getIdTokenResult();
    await this._auth.signOut();
    this.redirectAfterLogout(token);
  }

  public async verifyPasswordResetCode(oobCode: string): Promise<boolean> {
    try {
      await verifyPasswordResetCode(this._auth, oobCode);
      return true;
    } catch (e: any) {
      return false;
    }
  }

  public async confirmPasswordReset(
    oobCode: string,
    newPassword: string
  ): Promise<boolean> {
    try {
      await confirmPasswordReset(this._auth, oobCode, newPassword);
      return true;
    } catch (e: any) {
      return false;
    }
  }

  redirectAfterLogout(token: IdTokenResult | undefined) {
    if (!token) {
      this._ngZone.run(() => {
        void this._router.navigate(['/login']);
      });
      return;
    }

    let provider: any = null;
    if (!token.claims['is_SSO']) {
      provider = 'password';
    } else if (token.signInProvider) {
      const providers = Object.entries(environment.authProviders);
      const foundProvider = providers.find(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([key, value]) => value.provider === token.signInProvider
      );
      provider = foundProvider ? foundProvider[0] : null;
    }
    this._ngZone.run(() => {
      void this._router.navigate(['/login'], { queryParams: { provider } });
    });
  }
}
